/**
 * VaultFolderTree: Collapsible folder tree sidebar with color, visibility badges
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FolderPlus,
  Pencil,
  Trash2,
  Lock,
  Users,
  Settings,
  Palette,
} from 'lucide-react';
import type { VaultFolder } from '../../types/vault.types';
import { VaultColorPicker } from './VaultColorPicker';

interface VaultFolderTreeProps {
  allFolders: VaultFolder[];
  currentFolderId: string | null;
  currentUserId: string;
  onNavigate: (folderId: string | null) => void;
  onRename: (folderId: string, name: string) => Promise<void>;
  onDelete: (folderId: string) => void | Promise<void>;
  onCreateFolder: () => void;
  onCreateSubfolder: (parentFolder: VaultFolder) => void;
  onOpenSettings: (folder: VaultFolder) => void;
  onUpdateColor: (folderId: string, color: string) => Promise<void>;
  onMoveFile?: (fileId: string, targetFolderId: string) => Promise<void>;
  onMoveFolder?: (folderId: string, targetParentId: string) => Promise<void>;
}

export function VaultFolderTree({
  allFolders,
  currentFolderId,
  currentUserId,
  onNavigate,
  onRename,
  onDelete,
  onCreateFolder,
  onCreateSubfolder,
  onOpenSettings,
  onUpdateColor,
  onMoveFile,
  onMoveFolder,
}: VaultFolderTreeProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [contextMenu, setContextMenu] = useState<{ folder: VaultFolder; x: number; y: number } | null>(null);
  const [colorPickerFolderId, setColorPickerFolderId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Reposition context menu if it overflows viewport
  useEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const el = contextMenuRef.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let { x, y } = contextMenu;
    if (rect.right > vw - 8) x = vw - rect.width - 8;
    if (rect.bottom > vh - 8) y = vh - rect.height - 8;
    if (x < 8) x = 8;
    if (y < 8) y = 8;
    if (x !== contextMenu.x || y !== contextMenu.y) {
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
    }
  }, [contextMenu]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const getChildren = useCallback(
    (parentId: string | null) => allFolders.filter(f => f.parent_folder_id === parentId),
    [allFolders],
  );

  const handleRename = async (folderId: string) => {
    if (editName.trim()) {
      await onRename(folderId, editName);
    }
    setEditingId(null);
  };

  const handleContextMenu = (e: React.MouseEvent, folder: VaultFolder) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ folder, x: e.clientX, y: e.clientY });
    setColorPickerFolderId(null);
  };

  const getVisibilityBadge = (folder: VaultFolder) => {
    if (folder.visibility === 'private') {
      return <span title="Private"><Lock size={12} className="vault-visibility-badge private" /></span>;
    }
    if (folder.visibility === 'shared') {
      return <span title="Shared"><Users size={12} className="vault-visibility-badge shared" /></span>;
    }
    return null;
  };

  const renderFolder = (folder: VaultFolder, depth: number = 0) => {
    const children = getChildren(folder.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(folder.id);
    const isActive = currentFolderId === folder.id;
    const isEditing = editingId === folder.id;
    const folderColor = folder.color || '#3b82f6';

    return (
      <div key={folder.id}>
        <div
          className={`vault-folder-item ${isActive ? 'active' : ''} ${dropTargetId === folder.id ? 'drop-target' : ''}`}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
          onClick={() => {
            onNavigate(folder.id);
            if (hasChildren && !isExpanded) toggleExpand(folder.id);
          }}
          onContextMenu={(e) => handleContextMenu(e, folder)}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDropTargetId(folder.id); }}
          onDragLeave={(e) => {
            e.stopPropagation();
            // Only clear if truly leaving this element, not entering a child
            const related = e.relatedTarget as Node | null;
            if (related && e.currentTarget.contains(related)) return;
            setDropTargetId(null);
          }}
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const payload = e.dataTransfer.getData('text/plain');
            setDropTargetId(null);
            if (!payload) return;

            const [type, id] = payload.split(':');
            if (type === 'file' && id && onMoveFile) {
              await onMoveFile(id, folder.id);
            } else if (type === 'folder' && id && onMoveFolder) {
              if (id === folder.id) return; // can't drop into itself
              await onMoveFolder(id, folder.id);
            }
          }}
        >
          <button
            className="vault-folder-expand"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(folder.id);
            }}
            style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {isActive
            ? <FolderOpen size={16} className="vault-folder-icon" style={{ color: folderColor }} />
            : <Folder size={16} className="vault-folder-icon" style={{ color: folderColor }} />
          }

          {isEditing ? (
            <input
              className="vault-inline-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename(folder.id);
                if (e.key === 'Escape') setEditingId(null);
              }}
              onBlur={() => handleRename(folder.id)}
              maxLength={40}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <span className="vault-folder-name">{folder.name}</span>
              {getVisibilityBadge(folder)}
            </>
          )}
        </div>

        {isExpanded && (
          <div>
            {children.map(child => renderFolder(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const rootFolders = getChildren(null);

  return (
    <div className="vault-folder-tree" onClick={() => { setContextMenu(null); setColorPickerFolderId(null); }}>
      {/* Root "All Files" */}
      <div
        className={`vault-folder-item root ${currentFolderId === null ? 'active' : ''}`}
        onClick={() => onNavigate(null)}
      >
        <FolderOpen size={16} className="vault-folder-icon" />
        <span className="vault-folder-name" style={{ fontWeight: 600 }}>All Files</span>
      </div>

      {/* Folder tree */}
      {rootFolders.map(folder => renderFolder(folder, 0))}

      {/* New folder button */}
      <button
        className="vault-new-folder-btn"
        onClick={onCreateFolder}
      >
        <FolderPlus size={14} />
        <span>New Folder</span>
      </button>

      {/* Context menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="vault-context-menu"
          style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* New Subfolder: always available */}
          <button onClick={() => {
            onCreateSubfolder(contextMenu.folder);
            setContextMenu(null);
          }}>
            <FolderPlus size={14} /> New Subfolder
          </button>

          {contextMenu.folder.created_by === currentUserId && (
            <>
              <button onClick={() => {
                onOpenSettings(contextMenu.folder);
                setContextMenu(null);
              }}>
                <Settings size={14} /> Settings & Sharing
              </button>
              <button onClick={() => {
                setColorPickerFolderId(contextMenu.folder.id);
              }}>
                <Palette size={14} /> Change Color
              </button>

              {/* Inline color picker */}
              {colorPickerFolderId === contextMenu.folder.id && (
                <div className="vault-context-color-picker" onClick={e => e.stopPropagation()}>
                  <VaultColorPicker
                    selectedColor={contextMenu.folder.color || '#3b82f6'}
                    onSelect={async (hex) => {
                      await onUpdateColor(contextMenu.folder.id, hex);
                      setContextMenu(null);
                      setColorPickerFolderId(null);
                    }}
                    size="sm"
                  />
                </div>
              )}
            </>
          )}

          <div className="vault-context-divider" />

          <button onClick={() => {
            const folder = contextMenu.folder;
            setEditingId(folder.id);
            setEditName(folder.name);
            setContextMenu(null);
          }}>
            <Pencil size={14} /> Rename
          </button>

          {contextMenu.folder.created_by === currentUserId && (
            <button className="danger" onClick={() => {
              onDelete(contextMenu.folder.id);
              setContextMenu(null);
            }}>
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
